import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, Copy } from "lucide-react";
import { toast } from "sonner";
import { about } from "../data/content";
import {
  Starburst,
  TinyStar,
  Underline,
} from "../components/Stickers";

export default function About() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const handleCopy = async (value, successMessage) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
      } else {
        // Fallback for non-secure contexts
        const ta = document.createElement("textarea");
        ta.value = value;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success(successMessage, { duration: 1800 });
    } catch (e) {
      toast.error("לא הצלחתי להעתיק :(");
    }
  };

  return (
    <main data-testid="about-page" className="relative">
      {/* Hero */}
      <section className="mx-auto max-w-[1400px] px-5 md:px-10 lg:px-14 pt-16 md:pt-24 pb-12">
        <div className="flex items-center gap-3 mb-6">
          <span className="tag-pill">{about.pageTitle}</span>
          <span className="font-serif-editorial italic text-lg text-[color:var(--charcoal)]">
            {about.pageSubtitle}
          </span>
        </div>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="font-display font-black tracking-tight leading-[0.88] text-[13vw] md:text-[8vw] lg:text-[6.5vw] max-w-[18ch] relative"
          data-testid="about-headline"
        >
          {about.storyHeadline}
          <span className="text-[color:var(--pink)]">.</span>
          <Starburst
            className="hidden md:block absolute -top-6 -left-8 w-14 h-14"
            color="#FF1493"
          />
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          data-testid="about-headline-sub"
          className="mt-3 md:mt-4 font-serif-editorial italic text-xl md:text-2xl text-[color:var(--charcoal)]"
        >
          {about.storyHeadlineSub}
        </motion.p>

        {/* Contact block */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          data-testid="about-contact"
          className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 max-w-3xl"
        >
          <button
            type="button"
            onClick={() => handleCopy(about.contact.email, "המייל הועתק ✨")}
            data-testid="about-email"
            className="group ink-border rounded-2xl p-5 md:p-6 bg-[color:var(--paper)] hover:bg-[color:var(--pink)] hover:text-[color:var(--paper)] hover:border-[color:var(--pink)] transition-colors text-right cursor-pointer relative"
            aria-label="העתק כתובת מייל"
          >
            <div className="flex items-center gap-3 mb-3">
              <Mail
                size={20}
                className="opacity-70 group-hover:opacity-100"
                strokeWidth={2}
              />
              <span className="text-sm tracking-widest uppercase opacity-70">
                מייל
              </span>
              <Copy
                size={16}
                className="mr-auto opacity-0 group-hover:opacity-100 transition-opacity"
                strokeWidth={2}
              />
            </div>
            <p className="font-display font-bold text-xl md:text-2xl break-all leading-tight">
              {about.contact.email}
            </p>
            <p className="mt-2 text-sm font-serif-editorial italic opacity-80">
              {about.contact.emailNote}
            </p>
            <span className="block mt-3 text-xs tracking-wider uppercase opacity-60 group-hover:opacity-100 transition-opacity">
              לחצי להעתקה
            </span>
          </button>

          <button
            type="button"
            onClick={() => handleCopy(about.contact.phone, "המספר הועתק ✨")}
            data-testid="about-phone"
            className="group ink-border rounded-2xl p-5 md:p-6 bg-[color:var(--paper)] hover:bg-[color:var(--pink)] hover:text-[color:var(--paper)] hover:border-[color:var(--pink)] transition-colors text-right cursor-pointer relative"
            aria-label="העתק מספר טלפון"
          >
            <div className="flex items-center gap-3 mb-3">
              <Phone
                size={20}
                className="opacity-70 group-hover:opacity-100"
                strokeWidth={2}
              />
              <span className="text-sm tracking-widest uppercase opacity-70">
                פלאפון
              </span>
              <Copy
                size={16}
                className="mr-auto opacity-0 group-hover:opacity-100 transition-opacity"
                strokeWidth={2}
              />
            </div>
            <p
              className="font-display font-bold text-2xl md:text-3xl leading-tight"
              dir="ltr"
              style={{ textAlign: "right" }}
            >
              {about.contact.phone}
            </p>
            <span className="block mt-3 text-xs tracking-wider uppercase opacity-60 group-hover:opacity-100 transition-opacity">
              לחצי להעתקה
            </span>
          </button>
        </motion.div>

        {/* Section label */}
        <div className="mt-16 md:mt-24 flex items-center gap-3">
          <TinyStar className="w-4 h-4" color="#FF1493" />
          <span className="text-sm tracking-widest uppercase text-[color:var(--charcoal)]">
            {about.sectionLabel}
          </span>
        </div>
      </section>

      {/* Magazine body */}
      <article className="mx-auto max-w-[1100px] px-5 md:px-10 lg:px-14 pb-24 md:pb-32">
        <div className="space-y-12 md:space-y-16">
          {about.story.map((block, i) => (
            <StoryBlock key={i} block={block} index={i} />
          ))}
        </div>
      </article>
    </main>
  );
}

function StoryBlock({ block, index }) {
  const motionProps = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-50px" },
    transition: { duration: 0.6, delay: 0.05 },
  };

  if (block.type === "paragraph") {
    const inset = index % 3 === 1;
    return (
      <motion.div
        {...motionProps}
        data-testid={`about-paragraph-${index}`}
        className={inset ? "md:pr-[15%] md:pl-0" : "md:pl-[8%]"}
      >
        <p className="text-xl md:text-2xl leading-[1.7] text-[color:var(--ink)]">
          {block.text}
        </p>
      </motion.div>
    );
  }

  if (block.type === "pullquote") {
    return (
      <motion.blockquote
        {...motionProps}
        data-testid={`about-pullquote-${index}`}
        className="relative my-6 md:my-12"
      >
        <span className="absolute -top-6 right-0 font-serif-editorial text-[color:var(--pink)] text-7xl md:text-9xl leading-none">
          &ldquo;
        </span>
        <p className="pull-quote text-3xl md:text-5xl leading-[1.1] max-w-3xl text-[color:var(--ink)] pr-10 md:pr-16">
          {block.text}
        </p>
        <Underline
          className="absolute -bottom-2 right-10 w-40 h-3"
          color="#FF1493"
        />
      </motion.blockquote>
    );
  }

  return null;
}
